import { useEffect, useState } from 'react';
import { useDeleteMembershipMutation, useLazyGetMyMembershipsQuery } from '../../../app/features/project/projectApiSlice';
import type { FastProject } from '../../../types/interfaces';
import useErrorMessage from '../../../utils/useErrorMessage';

const useRemoveMeFromProject = (userId: number, refetch?: () => void) => {
  const [isRemoveDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState(false);
  const [projectToRemoveFrom, setProjectToRemoveFrom] = useState<FastProject | null>(null);

  const [getMyMemberships] = useLazyGetMyMembershipsQuery();
  const [deleteMembership, { isLoading: isLoadingRemove, isError: isErrorRemove, error: removeError }] = useDeleteMembershipMutation();

  const { errorMsg } = useErrorMessage({ isError: isErrorRemove, error: removeError });

  useEffect(() => {
    if (errorMsg)
      setIsErrorMessageOpen(true);
  }, [errorMsg]);


  const handleRemove = async () => {
    if (isLoadingRemove || !projectToRemoveFrom?.id) return;

    try {
      const memberships = await getMyMemberships({
        projectId: projectToRemoveFrom.id,
        userId
      }).unwrap();

      if (!memberships.length) {
        console.warn('No membership found for current user');
        return;
      }

      const promises = memberships.map((membership) => deleteMembership(membership.id).unwrap());
      await Promise.all(promises);
      if (refetch) refetch();
    } catch (error) {
      console.error('Failed to remove user from project:', error);
    } finally {
      setRemoveDialogOpen(false);
    }
  };

  const showRemoveModal = (project: FastProject) => {
    setRemoveDialogOpen(true);
    setProjectToRemoveFrom(project);
  };

  const handleCancel = () => {
    setRemoveDialogOpen(false);
  };

  return {
    isRemoveDialogOpen,
    handleCancel,
    handleRemove,
    showRemoveModal,
    isLoadingRemove,
    removeMeFromProjectErrorMsg: errorMsg,
    isRemoveMeFromProjectLoading: isLoadingRemove,
    isRemoveMeFromProjectErrorMessageOpen: isErrorMessageOpen,
    closeRemoveMeFromProjectErrorMessage: () => setIsErrorMessageOpen(false)
  };
};

export default useRemoveMeFromProject; 